import {
  lighten,
  mergeOpacity,
  isValidColor,
} from '@packages/systems/core/utils/ColorUtils';
import {black} from '@packages/utilities/inline-style-utils';
import {styled, tokens, type Theme} from '@packages/utilities/styled';
import {Icon as SpringIcon} from '@packages/systems/spring';
import clickable from './logic/clickable';
import type {CSSProperties} from '@packages/utilities/css-properties-type';

const HIGHLIGHT_AMOUNT = 5;
export const highlight = lighten(HIGHLIGHT_AMOUNT);
export const unhighlight = lighten(-HIGHLIGHT_AMOUNT);
const Clickable = clickable('div');

const controlReset = {
  paddingTop: 0,
  paddingBottom: 0,
  fontFamily: 'inherit',
  fontSize: 'inherit',
  outline: 0,
} as const;

type ThemingType = {
  theme: Theme;
  style: CSSProperties;
  blend?: boolean;
  risk?: boolean;
  primary?: boolean;
  warning?: boolean;
  subtle?: boolean;
  danger?: boolean;
  dynamic?: boolean;
  emphasis?: boolean;
  left?: boolean;
  right?: boolean;
  center?: boolean;
  segmented?: boolean;
  disabled?: boolean;
  sizing?: string;
  dropdown?: string;
  structure?: 'ghost' | string;
  density?: 'compact' | 'comfortable';
  link?: boolean;
  icon?: boolean;
};

export const buttonStyle = ({
  icon,
  theme: {control, Button},
  structure,
  style,
}: ThemingType) => ({
  ...controlReset,
  /**
   * Brightening up the base Icon color if it's an IconButton.
   * @see https://webflow.atlassian.net/browse/DS-407
   *
   * Following Design Guidelines:
   * @see https://www.figma.com/file/DeqT7heOOBYB3e1sY5zC4f/Spring-2.0-Documentation?type=design&node-id=150-147815&mode=design&t=NEE1IdGYS9Q16PB0-0
   */
  [SpringIcon.__experimentalTokens.color]:
    icon && structure !== 'ghost'
      ? tokens.colors.textPrimary
      : tokens.colors.textSecondary,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingRight: Button.paddingRight,
  paddingLeft: Button.paddingLeft,
  height: Button.buttonShellHeight,
  borderRadius: control.borderRadius,
  color: Button.color,
  background: control.backgroundColor,
  borderWidth: Button.borderWidth,
  borderStyle: 'solid',
  borderTopColor: control.borderColor,
  borderRightColor: control.borderColor,
  borderBottomColor: control.borderColor,
  borderLeftColor: control.borderColor,
  boxSizing: 'border-box',
  boxShadow: Button.boxShadow,
  alignSelf: 'center',
  ...style,
});

const blendStyles = {
  background: 'transparent',
  borderTopColor: 'transparent',
  borderRightColor: 'transparent',
  borderBottomColor: 'transparent',
  borderLeftColor: 'transparent',
} as const;

const noLeftBorder = {
  borderLeftWidth: 0,
} as const;

const noRightBorder = {
  borderRightWidth: 0,
} as const;

const noLeftOrRightBorders = {
  ...noLeftBorder,
  ...noRightBorder,
} as const;

const segmentedKeyboardFocusStyles = {zIndex: 1} as const;

export const buttonVariants = {
  primary: ({theme: {colors, Button}}: ThemingType) => ({
    [SpringIcon.__experimentalTokens.color]: Button.primary.color,
    background: Button.primary.background || colors.accentBright,
    color: Button.primary.color,
    borderTopColor: Button.primary.borderColor,
    borderRightColor: Button.primary.borderColor,
    borderBottomColor: Button.primary.borderColor,
    borderLeftColor: Button.primary.borderColor,
  }),
  warning: ({theme: {Button}}: ThemingType) => ({
    [SpringIcon.__experimentalTokens.color]: Button.primary.color,
    background: Button.warning.background,
    color: Button.warning.color,
    borderTopColor: Button.warning.borderColor,
    borderRightColor: Button.warning.borderColor,
    borderBottomColor: Button.warning.borderColor,
    borderLeftColor: Button.warning.borderColor,
  }),
  danger: ({theme: {colors, Button}}: ThemingType) => ({
    [SpringIcon.__experimentalTokens.color]: Button.danger.color,
    background: Button.danger.background || colors.dangerBackground,
    color: Button.danger.color,
    borderTopColor: Button.danger.borderColor,
    borderRightColor: Button.danger.borderColor,
    borderBottomColor: Button.danger.borderColor,
    borderLeftColor: Button.danger.borderColor,
  }),
  dynamic: ({theme: {colors, Button}}: ThemingType) => ({
    [SpringIcon.__experimentalTokens.color]: Button.dynamic.color,
    background: Button.dynamic.background || colors.dynamicBackground,
    color: Button.dynamic.color,
    borderTopColor: Button.dynamic.borderColor,
    borderRightColor: Button.dynamic.borderColor,
    borderBottomColor: Button.dynamic.borderColor,
    borderLeftColor: Button.dynamic.borderColor,
  }),
  emphasis: ({theme: {colors, Button}}: ThemingType) => ({
    [SpringIcon.__experimentalTokens.color]: Button.emphasis.color,
    background: Button.emphasis.background || colors.successBackground,
    color: Button.emphasis.color,
    borderTopColor: Button.emphasis.borderColor,
    borderRightColor: Button.emphasis.borderColor,
    borderBottomColor: Button.emphasis.borderColor,
    borderLeftColor: Button.emphasis.borderColor,
  }),
  subtle: ({theme: {Button}}: ThemingType) => ({
    [SpringIcon.__experimentalTokens.color]: Button.subtle.color,
    background: Button.subtle.background,
    color: Button.subtle.color,
    boxShadow: Button.subtle.boxShadow,
    borderWidth: Button.subtle.borderWidth,
    borderStyle: Button.subtle.borderStyle,
  }),
  blendDynamic: ({theme: {colors}}: ThemingType) => ({
    background: mergeOpacity(colors.dynamicBackground, 0.5),
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
  }),
  blend: blendStyles,
  overlay: ({theme: {colors, control}}: ThemingType) => ({
    background: colors.softShadow,
    color: control.textColor,
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
  }),
  disabled: ({theme: {Button}, subtle}: ThemingType) => {
    const styles = {
      cursor: 'not-allowed',
      ...Button.disabled,
    };

    if (subtle) {
      styles.borderColor = Button.subtle.borderColor;
    }

    return styles;
  },
  structure: ({theme: {colors}, structure}: ThemingType) => {
    if (structure === 'ghost') {
      return {
        background: 'transparent',
        border: 'none',
        color: colors.foregroundDim,
        boxShadow: 'none',
      };
    }

    return null;
  },
  hover: ({
    theme: {colors, control, Button, filters},
    style,
    blend,
    risk,
    primary,
    warning,
    danger,
    dynamic,
    emphasis,
    disabled,
    structure,
    subtle,
  }: ThemingType) => {
    const isPrimaryOnly =
      primary && !warning && !danger && !emphasis && !dynamic;
    const isValidBackgroundColor =
      // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
      isValidColor(style.background) &&
      // @ts-expect-error - TS2339 - Property 'includes' does not exist on type '{}'.
      !style.background?.includes?.('gradient');

    const styles: React.CSSProperties = {
      color: disabled
        ? dynamic
          ? Button.dynamic.color
          : Button.hover.disabledColor ||
            (style.color as string) ||
            Button.hover.color
        : isPrimaryOnly
          ? Button.hover.color
          : dynamic
            ? Button.dynamic.color
            : danger
              ? Button.hover.color
              : emphasis
                ? Button.hover.color
                : risk
                  ? Button.hover.color
                  : warning
                    ? Button.hover.color
                    : Button.hover.default,
      background: disabled
        ? (style.background as string)
        : risk
          ? Button.risk.background
          : blend
            ? Button.blend.backgroundHover
            : isPrimaryOnly
              ? Button.primary.backgroundHover
              : dynamic
                ? Button.dynamic.backgroundHover
                : danger
                  ? Button.danger.backgroundHover
                  : emphasis
                    ? Button.emphasis.backgroundHover
                    : risk
                      ? Button.risk.backgroundHover
                      : warning
                        ? Button.warning.backgroundHover
                        : subtle
                          ? Button.subtle.backgroundHover
                          : isValidBackgroundColor
                            ? // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'ColorInputWithoutInstance'.
                              highlight(style.background)
                            : control.backgroundColorHover,
      filter: 'unset',
    };

    // @ts-expect-error- TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'CSSProperties'.
    styles[SpringIcon.__experimentalTokens.color] = styles.color;

    if (risk) {
      styles.borderColor = Button.risk.borderColor;
    }

    if (structure === 'ghost') {
      styles.filter = filters.lighten;

      if (primary) {
        styles.background = colors.backgroundGhostPrimary;
      } else if (danger) {
        styles.background = colors.backgroundGhostDanger;
      } else {
        styles.background = colors.backgroundGhostDefault;
      }
    }

    if (subtle && !disabled) {
      styles.borderColor = Button.subtle.borderColorHover;
    }

    return styles;
  },
  selected: ({
    theme,
    structure,
    theme: {colors},
    left,
    right,
    center,
  }: ThemingType) => {
    const rightShadow = `-${theme.control.borderWidth}px 0 0 0 ${colors.borderDeep}`;
    const leftShadow = `${theme.control.borderWidth}px 0 0 0 ${colors.borderDeep}`;
    const isGhost = structure === 'ghost';

    const boxShadow = right
      ? rightShadow
      : left
        ? leftShadow
        : center
          ? `${leftShadow}, ${rightShadow}`
          : null;

    return {
      color: colors.foregroundVivid,
      background: isGhost
        ? theme.Button.selected.ghostBackground
        : colors.backgroundDeep,
      borderTopColor: colors.borderDeep,
      borderRightColor: colors.borderDeep,
      borderBottomColor: colors.borderDeep,
      borderLeftColor: colors.borderDeep,
      ...(boxShadow ? {boxShadow} : null),
    };
  },
  active: ({
    theme: {control, Button},
    style,
    blend,
    risk,
    primary,
    warning,
    danger,
    dynamic,
    emphasis,
    disabled,
    subtle,
  }: ThemingType) => ({
    background: disabled
      ? style.background
      : blend
        ? black(0.15)
        : primary && !warning && !danger && !emphasis && !dynamic
          ? Button.primary.background
          : dynamic
            ? Button.dynamic.background
            : danger
              ? Button.danger.background
              : emphasis
                ? Button.emphasis.background
                : risk
                  ? Button.risk.background
                  : warning
                    ? Button.warning.background
                    : subtle
                      ? Button.subtle.background
                      : // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
                        isValidColor(style.background)
                        ? style.background
                        : control.backgroundColor,
  }),
  keyboardFocus: ({theme, segmented}: ThemingType) => ({
    borderTopColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
    boxShadow: theme.focusColors.outline,
    ...(segmented ? segmentedKeyboardFocusStyles : null),
  }),
  segmented: ({left, right, center}: ThemingType) => {
    // Our segmented control uses `grid-gap` to fake borders between items
    if (left) {
      return noRightBorder;
    }
    if (right) {
      return noLeftBorder;
    }
    if (center) {
      return noLeftOrRightBorders;
    }
    return null;
  },
  unitBorder: () => ({
    paddingRight: 0,
    paddingLeft: 0,
  }),
  // This will force a transparent background even if disabled -- use for such cases
  transparentBackground: {...blendStyles, boxShadow: 'none'},
  left: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  right: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  center: {
    borderRadius: 0,
  },
  noLeftBorder: {
    borderLeftWidth: 0,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  noBorderLeftRadius: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  noBorder: {
    borderWidth: 0,
    borderStyle: 'none',
  },
  input: ({theme}: ThemingType) => ({
    borderTopColor: theme.colors.borderDeep,
    borderRightColor: theme.colors.borderDeep,
    borderBottomColor: theme.colors.borderDeep,
    borderLeftColor: theme.colors.borderDeep,
  }),
  link: ({theme}: ThemingType) => ({
    padding: 0,
    color: theme.colors.accentVivid,
    ...blendStyles,
  }),
  dropdown: ({dropdown, theme: {Button}}: ThemingType) => {
    if (dropdown === 'left') {
      return {
        ...noRightBorder,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      };
    } else if (dropdown === 'right') {
      return {
        ...noLeftBorder,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        width: Button.gutters.large * 2,
        padding: 0,
      };
    } else {
      return null;
    }
  },
  depth: ({theme: {shadows}}: ThemingType) => ({
    boxShadow: shadows.depth,
  }),
  density: ({density, theme: {Button}}: ThemingType) => {
    if (density !== 'comfortable') return;

    return {
      paddingLeft: Button.gutters.large,
      paddingRight: Button.gutters.large,
    };
  },
  sizing: ({icon, link, sizing, theme: {Button}}: ThemingType) => {
    const hasNoPadding = icon || link;

    if (sizing === 'large') {
      return {
        height: Button.size.large,
        paddingLeft: hasNoPadding ? 0 : Button.gutters.large,
        paddingRight: hasNoPadding ? 0 : Button.gutters.large,
        fontSize: Button.fontSize.large,
      };
    } else if (sizing === 'small') {
      return {
        height: Button.size.small,
        paddingLeft: hasNoPadding ? 0 : Button.gutters.small,
        paddingRight: hasNoPadding ? 0 : Button.gutters.small,
        fontSize: Button.fontSize.small,
      };
    }
    return null;
  },
  icon: ({sizing, theme: {Button}}: ThemingType) => {
    if (sizing === 'large') {
      return {
        width: Button.size.large,
        padding: 0,
      };
    } else if (sizing === 'small') {
      return {
        width: Button.size.small,
        padding: 0,
      };
    } else {
      return {
        padding: 0,
      };
    }
  },
  full: {
    width: '100%',
  },
} as const;

const ButtonShell = styled(Clickable)(buttonStyle, buttonVariants);

/**
 * @deprecated use `Button` from `@packages/systems/spring` instead
 */
export const RealButtonShell = styled(clickable('button'))(
  buttonStyle,
  buttonVariants
);

ButtonShell.displayName = 'ButtonShell';

export default ButtonShell;
